import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, Combobox, mergeClasses, Option, OptionGroup, Tooltip } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';

import { I18nContext } from '@bingads-webui-react/i18n-context';

import { useSearchableMultiSelectorComboboxStyles } from './styles';
import { FetchingStatus } from '../hooks/use-fetching-state';
import { BaseResultType } from '../types';

declare function _TL_(arg0: string): string;

const InvalidOptionValues = ['empty-option', 'loading-option', 'no-more-option', undefined];

export interface SearchableMultiSelectorComboboxProps<ResultType> {
  selected: ResultType[];
  results: ResultType[];
  status: FetchingStatus;
  hasMore?: boolean;
  disabled?: boolean;
  multiSelect?: boolean;
  allowSelectAll?: boolean;
  loadingText?: string;
  placeholderText?: string;
  noResultsText?: string;
  tooltipText?: string;
  resultsTitle?: string;
  onChangeQuery: (query: string) => void;
  onSelectOption: (option: ResultType) => void;
  onRenderOption?: (option: ResultType) => React.ReactNode;
  onCleanUp: () => void;
  onSelectAll: () => void;
  onFetchMore: (query: string) => void;
}

export const SearchableMultiSelectorCombobox = <ResultType extends BaseResultType, >({
  selected,
  results,
  status,
  hasMore,
  disabled,
  multiSelect,
  allowSelectAll,
  loadingText,
  placeholderText,
  noResultsText,
  tooltipText,
  resultsTitle,
  onChangeQuery,
  onSelectOption,
  onRenderOption,
  onCleanUp,
  onSelectAll,
  onFetchMore,
}: SearchableMultiSelectorComboboxProps<ResultType>) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useSearchableMultiSelectorComboboxStyles();

  const [query, setQuery] = useState('');

  const loadingOption = useMemo(() => (
    <Option key="loading-option" value="loading-option" className="loading-option" checkIcon={null}>
      {loadingText ?? i18n.getString(_TL_('Loading...'))}
    </Option>
  ), [i18n, loadingText]);

  const emptyOption = useMemo(() => (
    <Option key="empty-option" value="empty-option" className="empty-option" checkIcon={null}>
      {noResultsText ?? i18n.getString(_TL_('No results found'))}
    </Option>
  ), [i18n, noResultsText]);

  const noMoreOption = useMemo(() => (
    <Option key="no-more-option" value="no-more-option" className="no-more-option" checkIcon={null}>
      {noResultsText ?? i18n.getString(_TL_('No more item'))}
    </Option>
  ), [i18n, noResultsText]);

  const selectedOptions = useMemo(() => {
    const selectedIds = selected.map(({ id }) => id);
    const resultIds = results.map(({ id }) => id);

    return _.intersection(selectedIds, resultIds);
  }, [selected, results]);

  const TooltipWrapper = useCallback(({ children }) => {
    if (!tooltipText) { return children; }

    return (
      <Tooltip
        content={tooltipText}
        relationship="description"
        positioning="above-end"
      >
        {children}
      </Tooltip>
    );
  }, [tooltipText]);

  const updateQuery = useCallback((newQuery) => {
    setQuery(newQuery);
    onChangeQuery(newQuery);
  }, [onChangeQuery]);

  const handleBlur = useCallback(() => {
    setQuery('');
    onCleanUp();
  }, [onCleanUp]);

  const handleChange = useCallback(e => updateQuery(e.target.value), [updateQuery]);

  const handleOptionSelect = useCallback((_e, { optionValue }) => {
    if (InvalidOptionValues.includes(optionValue)) {
      return;
    }

    onSelectOption(results.find(({ id }) => id === optionValue)!);
  }, [onSelectOption, results]);

  const handleSearchBoxClick = useCallback(() => {
    if (!query) {
      onChangeQuery('');
    }
  }, [query, onChangeQuery]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && results.length === 0) {
      setQuery('');
      onCleanUp();
    } else if (e.key === 'Escape' || e.keyCode === 27) {
      setQuery('');
      onCleanUp();
    }
  }, [results.length, setQuery, onCleanUp]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (hasMore && target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      onFetchMore(query);
    }
  }, [onFetchMore, query, hasMore]);

  const renderLabel = useMemo(() =>
    resultsTitle && (
      <div className={classes.resultsTitleContainer}>
        {resultsTitle}
        {allowSelectAll && (
          <Button
            className={mergeClasses('select-all-button', classes.selectAllButton)}
            onClick={onSelectAll}
            appearance="transparent"
            style={{ padding: 0 }}
            aria-label={i18n.getString(_TL_('Select all'))}
          >
            {i18n.getString(_TL_('Select all'))}
          </Button>
        )}
      </div>
    ), [i18n, classes, resultsTitle, allowSelectAll, onSelectAll]);

  return (
    <TooltipWrapper>
      <Combobox
        className={mergeClasses('combobox', classes.root)}
        freeform
        multiselect={multiSelect}
        expandIcon={<SearchRegular />}
        selectedOptions={selectedOptions}
        placeholder={placeholderText ?? i18n.getString(_TL_('Search'))}
        disabled={disabled}
        value={query}
        open={status !== 'idle'}
        onBlur={handleBlur}
        onChange={handleChange}
        onOptionSelect={handleOptionSelect}
        onClick={handleSearchBoxClick}
        onKeyDown={handleKeyDown}
        listbox={{
          onScroll: handleScroll,
          className: 'listbox',
          // TODO:
          // 1. make the high configurable, the inline style is not supported here, need consider another way
          // 2. accessibliity support for the scroll action
        }}
        aria-label={placeholderText ?? i18n.getString(_TL_('Search'))}
      >
        {/**
         * Result options cannot be extracted onto a useMemo hook due to onRenderOption could have hook calls inside.
         * This will cause React to complain about hook calls inside a hook.
         */}
        {results.length > 0 && (
          <OptionGroup label={renderLabel}>
            {results.map(result => (
              <Option key={result.id} value={result.id} text={result.id} className={mergeClasses('result-option', `option-${result.id}`)}>
                {onRenderOption ? onRenderOption(result) : result.name}
              </Option>
            ))}
          </OptionGroup>
        )}
        {status === 'loading' && loadingOption}
        {results.length === 0 && status === 'loaded' && emptyOption}
        {results.length > 0 && status === 'loaded' && hasMore === false && noMoreOption}
      </Combobox>
    </TooltipWrapper>
  );
};

SearchableMultiSelectorCombobox.defaultProps = {
  disabled: false,
  hasMore: undefined,
  multiSelect: true,
  allowSelectAll: false,
  loadingText: undefined,
  placeholderText: undefined,
  noResultsText: undefined,
  tooltipText: undefined,
  onRenderOption: undefined,
  resultsTitle: undefined,
};
